import React, { memo } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Heading2,
  Input,
  Modal,
  Select,
  SpinnerBackdrop,
  Text,
  Toggle,
} from '@/components/elements';
import { exportUserData, UserExport } from '@roc-digital/mxm-base/logic';
import { useMethod } from '@roc-digital/ui-lib';
import { Option } from './Select';

export interface ExportUsersModalProps {
  headerContent?: any;
  onDownload?: (params: ExportParams) => Promise<void>,
  beforeCsv?: (data: UserExport[]) => Promise<UserExport[]>,
}

export function openExportUsersModal(props: ExportUsersModalProps) {
  Modal.open('mxm.modal', <ExportUsersModal {...props}/>, 'medium');
}

const offset = new Date().getTimezoneOffset() * 60;
function parse_date_string(v: string) {
  
  return Math.floor(new Date(v).getTime() / 1000) + offset;
}

const filterOptions: FilterOptions[] = [
  {param: 'last_active', paramLabel: 'Last Active', op: 'gte', inputType: 'date', opLabel: 'On or After', toParam: (v) => ({last_active_gte: parse_date_string(v)})},
  {param: 'last_active', paramLabel: 'Last Active', op: 'lte', inputType: 'date', opLabel: 'On or Before', toParam: (v) => ({last_active_lte: parse_date_string(v)})},
  {param: 'created', paramLabel: 'Created', op: 'gte', inputType: 'date', opLabel: 'On or After', toParam: (v) => ({created_gte: parse_date_string(v)})},
  {param: 'created', paramLabel: 'Created', op: 'lte', inputType: 'date', opLabel: 'On or Before', toParam: (v) => ({created_lte: parse_date_string(v)})},
  {param: 'phone', paramLabel: 'Phone', op: 'has', opLabel: 'Has Phone', inputType: 'boolean', toParam: (v) => ({has_phone: v === "true" || v === true ? true : false})},

  {param: 'web', paramLabel: 'Last Active on Web', op: 'gte', inputType: 'date', opLabel: 'On or After', toParam: (v) => ({web_gte: parse_date_string(v)})},
  {param: 'web', paramLabel: 'Last Active on Web', op: 'lte', inputType: 'date', opLabel: 'On or Before', toParam: (v) => ({web_gte: parse_date_string(v)})},

  {param: 'ios', paramLabel: 'Last Active on iOS', op: 'gte', inputType: 'date', opLabel: 'On or After', toParam: (v) => ({ios_gte: parse_date_string(v)})},
  {param: 'ios', paramLabel: 'Last Active on iOS', op: 'lte', inputType: 'date', opLabel: 'On or Before', toParam: (v) => ({ios_lte: parse_date_string(v)})},

  {param: 'android', paramLabel: 'Last Active on Android', op: 'gte', inputType: 'date', opLabel: 'On or After', toParam: (v) => ({android_gte: parse_date_string(v)})},
  {param: 'android', paramLabel: 'Last Active on Android', op: 'lte', inputType: 'date', opLabel: 'On or Before', toParam: (v) => ({android_lte: parse_date_string(v)})},

  {param: 'subscription_ends', paramLabel: 'Subscription', op: 'gte', inputType: 'date', opLabel: 'Expires On or After', toParam: (v) => ({sub_gte: parse_date_string(v)})},
  {param: 'subscription_ends', paramLabel: 'Subscription', op: 'lte', inputType: 'date', opLabel: 'Expires On or Before', toParam: (v) => ({sub_lte: parse_date_string(v)})},
]

interface ExportParams {
  last_active_gte?: number;
  last_active_lte?: number;
  web_gte?: number;
  web_lte?: number;
  ios_gte?: number;
  ios_lte?: number;
  android_gte?: number;
  android_lte?: number;
  sub_gte?: number;
  sub_lte?: number;
  has_email?: boolean;
  has_phone?: boolean;
  created_gte?: number;
  created_lte?: number;
}

function convertFiltersToExportParams(filters: Filter[]) {
  const params: ExportParams = {};

  filters.forEach(filter => {

    const option = filterOptions.find(fo => fo.op === filter.op && fo.param == filter.param);

    if(!option?.toParam) return;
    Object.assign(params, option.toParam(filter.value))

  })

  return params;
}

export const ExportUsersModal = memo((props: ExportUsersModalProps) => {

  const [filters, setFilters] = React.useState<Filter[]>([]);

  const doDownload = React.useCallback(async (params: ExportParams) => {
    if(props.onDownload) {
      await props.onDownload(params);
    }

    await downloadCSV(params, props.beforeCsv)
  }, []);

  const query = useMethod(doDownload);

  const closeModal = () => {
    Modal.close('mxm.modal');
  };

  return (
    <div className="p-8">
      <Heading2 className={'mb-8 text-admin-dark !font-extrabold !text-[26px]'}>Export User Data</Heading2>
      {props.headerContent ? <div className='mb-2'>{props.headerContent}</div> : null}
      <FilterInput value={filters} onChange={setFilters} options={filterOptions}/>
      <div>
        This action will download all user accounts as a CSV file. The file columns are as follows:
        <br/><br/>
        <pre>
           - <b>id</b>: User id <br/>
           - <b>email</b>: User email address, if known.<br/>
           - <b>created</b>: The date when the account was created.<br/>
           - <b>last_active</b>: The date when the user was last online.<br/>
           - <b>web</b>: The date when the user last visited mxmnews.com, if at all.<br/>
           - <b>ios</b>: The date when the user last opened the ios app, if at all.<br/>
           - <b>android</b>: The date when the user last opened the android app, if at all.<br/>
           - <b>subscription_status</b>: The current status of the subscription.<br/>
           - <b>subscription_status_hint</b>: A reason for the subscription status.<br/>
           - <b>subscription_end</b>: The date when the user's subscription will expire if not renewed.<br/>
           - <b>utm_url</b>: The utm url visited by the user before signing up.<br/>
           - <b>utm_referrer_url</b>: The referrer url of the utm_url.<br/>
           - <b>phone</b>: The phone number the user provided.<br/>
           - <b>signup_article_id</b>: The last article visited before signing up.<br/>
           - <b>comments</b>: Total number of comments created by the user. Includes deleted comments.<br/>
           - <b>tags</b>: Tags currently selected by the user. Tags are delimited by a semicolon.<br/>
        </pre>        
      </div>

      <div>
        <div className="mt-4 flex inline-block gap-2">
          <Button clickEvent={closeModal} size={ButtonSizes.medium} variant={ButtonVariants.secondary}>Cancel</Button>
          <Button clickEvent={() => query.run(convertFiltersToExportParams(filters))} disabled={query.loading} size={ButtonSizes.medium}>Download</Button>
        </div>
      </div>

      <SpinnerBackdrop active={query.loading}/>
    </div>
  );
});

async function downloadCSV(params: ExportParams, beforeCsv?: (data: UserExport[]) => Promise<UserExport[]> ) {
  let data = await exportUserData(params);
  if(beforeCsv) {
    data = await beforeCsv(data);
  }
  const filename = `mxm-users-export-${new Date().toLocaleString().replace(/\/|\\|\-|\s|\,/g, '_')}.csv`;

  const headers = 'id,email,created,last_active,web,ios,android,subscription_end,subscription_status,subscription_status_hint,utm_url,utm_referrer_url,phone,signup_article_id,comments,category_tags,topic_tags,location_tags';
  let content = '';
  const LOCATION = 'location:';
  const TOPIC = 'topic:';
  const CATEGORY = 'category:';

  for(let i = 0; i < data.length; i++) {
    const row = data[i];

    const tags = (typeof row.tags === 'string' ? row.tags : '').split(';');
    let categoryTags = '';
    let locationTags = '';
    let topicTags = '';

    for(let tag of tags) {
      if(tag.slice(0, LOCATION.length) === LOCATION) {
        if(categoryTags.length) categoryTags += ';'
        categoryTags+=tag.slice(LOCATION.length);
      } else if(tag.slice(0, TOPIC.length) === TOPIC) {
        if(topicTags.length) topicTags += ';'
        topicTags+=tag.slice(TOPIC.length);
      } else if(tag.slice(0, CATEGORY.length) === CATEGORY) {
        if(locationTags.length) locationTags += ';'
        locationTags+=tag.slice(LOCATION.length);
      }      
    }

    content+= '\n'
      + `${row.id || ''},`
      + `${row.email || ''},`
      + `${unixToLocalDate(row.created)},`
      + `${unixToLocalDate(row.last_active)},`
      + `${unixToLocalDate(row.web)},`
      + `${unixToLocalDate(row.ios)},`
      + `${unixToLocalDate(row.android)},`
      + `${unixToLocalDate(row.subscription_end)},`
      + `${row.subscription_status || ''},`
      + `${row.subscription_status_hint || ''},`
      + `${row.utm_url || ''},`
      + `${row.utm_referrer_url || ''},`
      + `${row.phone || ''},`
      + `${row.signup_article_id || ''},`
      + `${row.comments || '0'},`
      + `${categoryTags || ''},`
      + `${topicTags || ''},`
      + `${locationTags || ''}`
      
  }

  const blob = new Blob([headers+content], {type: 'text/csv'});
  const a = document.createElement('a');
  a.download = filename;
  a.href = window.URL.createObjectURL(blob);
  a.click();
}

function unixToLocalDate(unix: number) {
  if(!unix) return '';
  return new Date(unix * 1000).toLocaleString().replace(',', ' ');
}


interface FilterOptions {
  param: string;
  paramLabel: string;
  op: string;
  opLabel: string;
  inputType: 'date' | 'text' | 'boolean' | 'none';
  inputOptions?: string[];
  optionsLoader?: () => Promise<string>;
  toParam? (value?: any): ExportParams;
}

interface Filter {
  id: string;
  param: string;
  op: string;
  value: any;
}

interface FilterInputProps {
  value?: Filter[];
  onChange: (filters: Filter[]) => void;
  options: FilterOptions[];
}
function FilterInput(props: FilterInputProps) {
  const filters = props.value || [];
  const filtersRef = React.useRef(filters);
  filtersRef.current = filters;
  const addFilter = () => {
    const current = [...filtersRef.current];
    const nextOption = props.options.find(option => current.find(c => c.param !== option.param)) || props.options[0];

    if(!nextOption) return;
    const newGuy =  {op: nextOption.op, param: nextOption.param, value: null, id: Math.random().toString()};
    const filters: Filter[] = [
      ...current,
      newGuy
    ];
    props.onChange(filters);
  }
  const removeFilter = (id: string) => {
    const filters: Filter[] = [
      ...filtersRef.current,
    ];
    props.onChange(filters.filter(f => f.id !== id));
  }

  const replaceFilter = (id: string, change: Filter) => {
    const filters: Filter[] = [
      ...filtersRef.current,
    ];
    filters.splice(filters.findIndex(f => f.id === id), 1, change);
    props.onChange(filters);
  }

  return <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
    {filters.map((filter) => (
      <div key={filter.id} className='flex flex-row' style={{gap: 4, alignItems: 'center'}}>
        <RenderFilterInput value={filter} onChange={(change) => replaceFilter(filter.id, change)} options={props.options}/>
        <Button size='sm' clickEvent={() => removeFilter(filter.id)}>Remove</Button>
      </div>
    ))}
    <div>
      <Button size='sm' clickEvent={addFilter}>Add Filter</Button>
    </div>
  </div>
}

interface RenderFilterInputProps {
  value?: Filter;
  onChange: (optio: Filter) => void;
  options: FilterOptions[];
}

function RenderFilterInput(props: RenderFilterInputProps) {

  const valueRef = React.useRef(props.value);
  valueRef.current = props.value;

  const setFilter = (filter: Partial<Filter>) => {
    const current: Filter = {
      id: valueRef.current?.id || Math.random().toString(),
      param: valueRef.current?.param || '',
      op: valueRef.current?.op || '',
      value: valueRef.current?.value,
    }

    const next: Filter = {
      ...current,
      ...filter, 
    }

    if(filter.param) {
      const options = props.options.filter(o => o.param === filter.param);
      if(!options.find(o => o.op === next.op) && options.length) {
        next.op = options[0].op;
        // next.value = null;
      }
    }
  
    props.onChange(next)
  }
  
  const options = props.options;
  const filterParams = React.useMemo(() => {
    return options.filter(f => props.value?.param === f.param);
  }, [options, props.value?.param]);

  const option = options.find(f => f.param === props.value?.param && f.op == props.value?.op);

  const selectOptions = React.useMemo(() => {
    return options.map(option => {
      const o: Option = {
        label: option.paramLabel,
        value: option.param,
      }
      return o;
    })
    .filter((p, i, l) => l.findIndex(pp => pp.value === p.value) === i)
  }, [options]);

  const opOptions = React.useMemo(() => {
    return filterParams.map(option => {
      const o: Option = {
        value: option.op,
        label: option.opLabel,
      }
      return o;
    })
  }, [filterParams]);


  const input = React.useMemo(() => {
    if(option?.inputType === 'none') return null;

    if(option?.inputType === 'date') {
      return <Input size='small' value={props.value?.value || ''} type='date' changeEvent={(event) => setFilter({value: event.target.value})}/>
    } else if(option?.inputType === 'boolean') {
      return <Toggle initialState={props.value?.value || false}  label='' onToggle={(t) => setFilter({value: t})}/>
    } else {
      return <Input size='small'  value={props.value?.value || ''} type='text' changeEvent={(event) => setFilter({value: event.target.value})}/>
    }
  }, [option, props.value?.value])

  return <div className='flex flex-row' style={{gap: 4, alignItems: 'center'}}>
    <Select
      size='small'
      options={selectOptions}
      value={props.value?.param}
      changeEvent={event => setFilter({param: event.target.value})}
    />
    {opOptions.length === 1 ? (
      <span className='font-bold'>{opOptions[0].label}</span>
    ) : (
      <Select
        size='small'
        options={opOptions}
        value={props.value?.op}
        changeEvent={event => setFilter({op: event.target.value})}
      />
    )}
    {input}
  </div>
}

